<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import registrationsHttpService from '@/services/http/registrationsHttpService.ts'
import DocumentScansTable from './DocumentScansTable.vue'
import type { DocumentScansFilters } from './documentScansFilters.ts'
import type { DocumentScansSummary } from '@/services/http/documentScansHttpService.ts'

definePage({ meta: { allowedRoles: [Roles.Admin, Roles.Manager] } })

const { t, locale } = useI18n()

const search = ref('')
const searchDebounced = ref('')
let searchTimer: ReturnType<typeof setTimeout> | undefined
watch(search, (value) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    searchDebounced.value = value
  }, 300)
})

// Фильтр по умолчанию не задан; воронка сверху показывает разбивку по статусам и позволяет
// отфильтровать кликом (проблемные Infected/Missing сразу видны по счётчикам).
const status = ref<string | null>(null)
const eventId = ref<string | null>(null)

const statusOptions = computed(() => [
  { label: t('documentScans.statusPending'), value: 'Pending' },
  { label: t('documentScans.statusClean'), value: 'Clean' },
  { label: t('documentScans.statusInfected'), value: 'Infected' },
  { label: t('documentScans.statusError'), value: 'Error' },
  { label: t('documentScans.statusMissing'), value: 'Missing' },
  { label: t('documentScans.funnel.deleted'), value: 'Deleted' },
])

const filters = computed<DocumentScansFilters>(() => ({
  status: status.value || undefined,
  eventId: eventId.value || undefined,
  search: searchDebounced.value.trim() || undefined,
}))

const eventsQuery = useQuery({
  queryKey: computed(() => ['admin', 'events', locale.value]),
  queryFn: () => registrationsHttpService.getEvents(),
})
const eventOptions = computed(() =>
  (eventsQuery.data.value ?? []).map((e) => ({
    label: `${e.name} — ${new Date(e.startsAt).toLocaleDateString('ru-RU')}`,
    value: e.id,
  })),
)

// Воронка: сводка прокидывается снизу (DocumentScansTable) — по scope события/поиска до фильтра статуса.
const summary = ref<DocumentScansSummary | null>(null)

interface FunnelBox {
  key: string
  label: string
  active: number // основное число (неудалённые для статусов; всего для «Всего»/«Удалён»)
  total: number // при total>active показываем «/total» (напр. Заражён 0/1)
  code: string | null // фильтр: статус-код, 'Deleted' (удалённые) или null (Всего — сброс)
  color: string
}

const funnelBoxes = computed<FunnelBox[]>(() => {
  const s = summary.value
  if (!s) return []
  const box = (
    key: string,
    label: string,
    c: { active: number; total: number },
    code: string,
    color: string,
  ): FunnelBox => ({ key, label, active: c.active, total: c.total, code, color })
  return [
    {
      key: 'total',
      label: t('documentScans.funnel.total'),
      active: s.total,
      total: s.total,
      code: null,
      color: '',
    },
    box(
      'Pending',
      t('documentScans.statusPending'),
      s.pending,
      'Pending',
      'text-sky-600 dark:text-sky-400',
    ),
    box(
      'Clean',
      t('documentScans.statusClean'),
      s.clean,
      'Clean',
      'text-green-600 dark:text-green-400',
    ),
    box(
      'Infected',
      t('documentScans.statusInfected'),
      s.infected,
      'Infected',
      'text-red-600 dark:text-red-400',
    ),
    box(
      'Error',
      t('documentScans.statusError'),
      s.error,
      'Error',
      'text-amber-600 dark:text-amber-400',
    ),
    box('Missing', t('documentScans.statusMissing'), s.missing, 'Missing', 'text-surface-500'),
    {
      key: 'deleted',
      label: t('documentScans.funnel.deleted'),
      active: s.deleted,
      total: s.deleted,
      code: 'Deleted',
      color: 'text-surface-500',
    },
  ]
})

function pickStatus(box: FunnelBox) {
  status.value = box.code === status.value ? null : box.code // тоггл; «Всего» (code=null) сбрасывает
}

function isActive(box: FunnelBox): boolean {
  if (box.key === 'total') return !status.value
  return box.code === status.value
}
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <h1 class="text-xl font-bold">{{ t('documentScans.title') }}</h1>

    <!-- Воронка: клик по боксу фильтрует список; «Всего» сбрасывает. Число — неудалённые, «/N» — всего. -->
    <div v-if="summary" class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 w-full">
      <button
        v-for="box in funnelBoxes"
        :key="box.key"
        type="button"
        class="flex flex-col gap-1 rounded border p-3 text-left transition-colors"
        :class="[
          isActive(box)
            ? 'border-primary bg-primary/5'
            : 'border-surface-200 dark:border-surface-700',
          'cursor-pointer hover:border-primary',
        ]"
        :data-test="`funnel-${box.key}`"
        @click="pickStatus(box)"
      >
        <span class="text-xs text-surface-500">{{ box.label }}</span>
        <span class="text-2xl font-bold" :class="box.color">
          {{ box.active
          }}<span v-if="box.total > box.active" class="text-base font-normal text-surface-400"
            >/{{ box.total }}</span
          >
        </span>
      </button>
    </div>

    <div class="flex flex-wrap gap-3 items-end w-full">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500">{{ t('documentScans.searchLabel') }}</label>
        <InputText
          v-model="search"
          size="small"
          :placeholder="t('documentScans.searchPlaceholder')"
          data-test="document-scans-search"
          style="width: 16rem"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500">{{ t('documentScans.statusLabel') }}</label>
        <Select
          v-model="status"
          size="small"
          :options="statusOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('documentScans.allOption')"
          show-clear
          data-test="document-scans-status"
          style="width: 14rem"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500">{{ t('documentScans.event') }}</label>
        <Select
          v-model="eventId"
          size="small"
          :options="eventOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('documentScans.allOption')"
          show-clear
          filter
          data-test="document-scans-event"
          style="width: 18rem"
        />
      </div>
    </div>

    <DocumentScansTable :filters="filters" @update:summary="summary = $event" />
  </section>
</template>
